import React, { useState, useEffect, useCallback, useMemo } from "react";
import axiosInstance from "../source/axiosInstance";
import PropertyList from "../components/PropertyList/PropertyList";
import PropertyFormImproved from "../components/PropertyForm/PropertyFormImproved";
import DOMPurify from "dompurify";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { server } from "../util/server";
import debounce from "lodash.debounce";
import "./Home.css";

function Home() {
  const [properties, setProperties] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(9);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  const [status, setStatus] = useState("");
  const [highlight, setHighlight] = useState("");

  const [editProperty, setEditProperty] = useState(null);

  const navigate = useNavigate();

  // Fetch properties function
  const fetchProperties = useCallback(async () => {
    try {
      setLoading(true);
      const sanitizedTerm = DOMPurify.sanitize(search);

      console.log('🔍 Buscando imóveis...', {
        page: currentPage,
        limit: itemsPerPage,
        search: sanitizedTerm
      });

      const response = await axiosInstance.get(
        `${server}/api/imoveis/painel/p/imoveis`,
        {
          params: {
            page: currentPage,
            limit: itemsPerPage,
            status,
            highlight,
            search: sanitizedTerm,
            category,
          },
        }
      );

      setProperties(response.data.data);
      setTotalPages(response.data.totalPages);
      console.log(`✅ ${response.data.data.length} imóveis carregados`);
    } catch (error) {
      console.error('❌ Erro ao buscar imóveis:', error);
      toast.error('Erro ao carregar imóveis');
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, status, highlight, search, category]);

  // Debounced fetch for search
  const debouncedFetch = useMemo(
    () => debounce(fetchProperties, 500),
    [fetchProperties]
  );

  useEffect(() => {
    // Se for busca, usar debounce; caso contrário, buscar imediatamente
    if (search) {
      debouncedFetch();
    } else {
      fetchProperties();
    }

    // Cleanup
    return () => {
      debouncedFetch.cancel();
    };
  }, [currentPage, itemsPerPage, status, highlight, search, category]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const handleCategoryChange = (e) => {
    setCategory(e.target.value);
    setCurrentPage(1);
  };

  const handleDestaqueChange = async (property, novoDestaque) => {
    if (!window.confirm("Tem certeza que deseja alterar o tipo de destaque?")) {
      return;
    }

    try {
      const response = await axiosInstance.put(
        `${server}/api/imoveis/${property._id}/destaque`,
        {
          destaque: novoDestaque,
        }
      );

      if (response.status === 200) {
        const updatedProperties = properties.map((p) =>
          p._id === property._id ? { ...p, destaque: novoDestaque } : p
        );
        toast.success("Tipo de destaque atualizado com sucesso.");
        fetchProperties();
      }
    } catch (error) {
      toast.error(
        "Erro ao atualizar tipo de destaque. Por favor, tente novamente."
      );
      console.error("Erro ao atualizar tipo de destaque:", error);
    } finally {
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleEdit = (property) => {
    setEditProperty(property);
  };

  const toggleHighlight = async (property) => {
    try {
      toast.info("Aguarde um pouco...");
      const response = await axiosInstance.put(
        `${server}/api/imoveis/${property._id}/toggle-highlight`
      );
      property.highlight = response.data.highlight;
      fetchProperties();
    } catch (error) {
      console.error("Error toggling highlight:", error);
    }
  };

  const toggleHighlightImovelWeb = async (property) => {
    try {
      toast.info("Aguarde um pouco...");
      const response = await axiosInstance.put(
        `${server}/api/imoveis/${property._id}/toggle-highlight-imovelweb`
      );
      property.highlight = response.data.highlight;
      fetchProperties();
    } catch (error) {
      console.error("Error toggling highlight:", error);
    }
  };

  const toggleStatus = async (property) => {
    try {
      toast.info("Aguarde um pouco...");
      const response = await axiosInstance.put(
        `${server}/api/imoveis/${property._id}/toggle-status`
      );
      property.status = response.data.status;
      fetchProperties();
    } catch (error) {
      console.error("Error toggling status:", error);
    }
  };

  const downloadImages = async (property) => {
    try {
      toast.info("Aguarde um pouco...");
      const response = await axiosInstance.get(
        `${server}/api/imoveis/${property._id}/download-images`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${property.code}_images.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error downloading images:", error);
    }
  };

  return (
    <div className="container mt-5">
      {editProperty ? (
        <PropertyFormImproved
          property={editProperty}
          setEditProperty={setEditProperty}
        />
      ) : (
        <>
          <div
            style={{ display: "flex", justifyContent: "space-between" }}
            className="tittle-container"
          >
            <h2>Lista de Propriedades</h2>
            <button
              className="btn btn-success"
              onClick={() => navigate("/new-property")}
            >
              Criar Propriedade
            </button>
          </div>
          <br />
          <div className="mb-3 position-relative">
            <input
              type="text"
              className="form-control"
              placeholder="Buscar propriedades (código, título, localização...)"
              value={search}
              onChange={handleSearch}
              disabled={loading}
            />
            {loading && (
              <div 
                className="spinner-border spinner-border-sm position-absolute" 
                style={{ right: '10px', top: '50%', transform: 'translateY(-50%)' }}
                role="status"
              >
                <span className="visually-hidden">Buscando...</span>
              </div>
            )}
          </div>
          <div className="mb-3">
            <select
              className="form-control"
              value={category}
              onChange={handleCategoryChange}
            >
              <option value="">Todos</option>
              <option value="Áreas industriais a Venda">
                Áreas industriais
              </option>
              <option value="Chácaras a Venda">Chácaras</option>
              <option value="Condomínios a Venda">Condomínios</option>
              <option value="Fazendas a Venda">Fazendas</option>
              <option value="Haras a Venda">Haras</option>
              <option value="Represa a Venda">Represas</option>
              <option value="Sítios a Venda">Sítios</option>
              <option value="Terrenos a Venda">Terrenos</option>
            </select>
          </div>
          <div className="mb-3">
            <select
              className="form-control"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">Todos os Status</option>
              <option value="A">Ativos</option>
              <option value="I">Inativos</option>
            </select>
          </div>
          <div className="mb-3">
            <select
              className="form-control"
              value={highlight}
              onChange={(e) => {
                setHighlight(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">Todos os Destaques</option>
              <option value="true">Destacados</option>
              <option value="false">Não Destacados</option>
            </select>
          </div>
          <div className="mb-3">
            <select
              className="form-control"
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(parseInt(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={75}>75</option>
              <option value={100}>100</option>
            </select>
          </div>
          {loading ? (
            <div className="text-center my-5">
              <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                <span className="visually-hidden">Carregando...</span>
              </div>
              <p className="mt-3 text-muted">Carregando imóveis...</p>
            </div>
          ) : properties.length === 0 ? (
            <div className="alert alert-info text-center" role="alert">
              <h4 className="alert-heading">Nenhum imóvel encontrado</h4>
              <p>Tente ajustar os filtros ou criar um novo imóvel.</p>
            </div>
          ) : (
            <PropertyList
              properties={properties}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              onEdit={handleEdit}
              downloadImages={downloadImages}
              toggleHighlight={toggleHighlight}
              toggleStatus={toggleStatus}
              toggleHighlightImovelWeb={toggleHighlightImovelWeb}
              handleDestaqueChange={handleDestaqueChange}
              loading={loading}
            />
          )}
        </>
      )}
    </div>
  );
}

export default Home;
